import type { ReactNode } from 'react';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

export interface AlertProps {
  tone?: AlertTone;
  /** Kurze Ueberschrift. Ohne sie steht nur der Fliesstext. */
  title?: ReactNode;
  children: ReactNode;
  /** Ein Knopf, der aus dem Hinweis heraus weiterhilft. */
  action?: ReactNode;
}

const TONE_PREFIX: Record<AlertTone, string> = {
  info: 'Hinweis',
  success: 'Erledigt',
  warning: 'Achtung',
  danger: 'Fehler',
};

/**
 * Hinweis mit Tonfall.
 *
 * Zwei Festlegungen tragen die Barrierefreiheit:
 *
 *   1. Die Farbe traegt die Bedeutung NIE allein. Jeder Hinweis beginnt mit
 *      einem Wort, das den Tonfall benennt — sonst ist er fuer jeden vierten
 *      Mann mit Rotgruenschwaeche nur ein grauer Kasten.
 *   2. Fehler und Warnungen melden sich als role="alert" und werden vom
 *      Screenreader sofort vorgelesen; Erfolg und Hinweis als role="status",
 *      damit sie ein Vorlesen nicht unterbrechen.
 */
export function Alert({ tone = 'info', title, children, action }: AlertProps) {
  const dringend = tone === 'danger' || tone === 'warning';

  return (
    <div className="alert" data-tone={tone} role={dringend ? 'alert' : 'status'}>
      <div className="alert__body">
        <p className="alert__title">
          <span className="alert__prefix">{TONE_PREFIX[tone]}</span>
          {title ? <> — {title}</> : null}
        </p>
        <div className="alert__text">{children}</div>
      </div>
      {action && <div className="alert__action">{action}</div>}
    </div>
  );
}
